import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Badge, Button, Card, Col, Form, Modal, Row, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import io from 'socket.io-client'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import { useLoading } from '../contexts/LoadingContext'
import axiosInstance from '../services/axiosInterceptor'
import { useAuth } from '../services/authProvider'
import { getTrackingRoleLabel, getLevelSuffix } from '../utils/roleLabels'

// รูปนักเรียน (hover ดูภาพใหญ่ + คลิกเปิด modal) — แพทเทิร์นเดียวกับหน้ารายงานอื่น
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '')
const StudentAvatar = ({ photo, name, size = 32 }) => {
  const [hovered, setHovered] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const initial = name ? name.trim().charAt(0) : '?'
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null
  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 }
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => { if (src) { e.stopPropagation(); setShowModal(true) } }}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>}
        {src && hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

// สี/ป้ายระดับความเสี่ยงรวม (แดง/เหลือง/เขียว)
const RISK_META = {
  red:    { bg: 'danger',  label: 'เสี่ยงสูง' },
  yellow: { bg: 'warning', label: 'เฝ้าระวัง' },
  green:  { bg: 'success', label: 'ปกติ' },
}

const AxisBadge = ({ level, children }) => {
  const meta = RISK_META[level] || RISK_META.green
  return <Badge bg={meta.bg} text={meta.bg === 'warning' ? 'dark' : undefined} className="fs-xs">{children}</Badge>
}

const InfoTip = ({ text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
    <i className="ri-information-line ms-1 text-primary" style={{ cursor: 'help' }} />
  </OverlayTrigger>
)

// ป้ายข้อมูลห้อง (ใช้ในการ์ดมือถือ) — label เล็กด้านบน + ค่าด้านล่าง
const MetaChip = ({ label, children }) => (
  <div className="text-center px-2">
    <div className="fs-xs text-muted">{label}</div>
    <div className="fs-sm fw-semibold">{children}</div>
  </div>
)

// การ์ดนักเรียน 1 คน แบบเรียบง่าย (อวตารเล็ก) — รวมสัญญาณเดียวกับตาราง
const StudentRiskCardSimple = ({ student: s, index, onClick }) => (
  <div className="border rounded p-3 shadow-sm" style={{ cursor: 'pointer' }} onClick={onClick}>
    <div className="d-flex align-items-start gap-2">
      <StudentAvatar photo={s.st_photo} name={s.st_name} size={40} />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="fw-semibold text-truncate">{s.st_name}</div>
          <AxisBadge level={s.overall}>{RISK_META[s.overall]?.label}</AxisBadge>
        </div>
        <div className="fs-xs text-muted">#{index + 1} · {s.st_idnumber}</div>
      </div>
    </div>

    <div className="d-flex flex-wrap justify-content-start gap-1 mt-2 pt-2 border-top">
      <MetaChip label="ห้องสามัญ">{s.class_room}</MetaChip>
      <MetaChip label="ห้องศาสนา">{s.religious_room || <span className="text-muted">-</span>}</MetaChip>
      <MetaChip label="หอพัก">
        {s.dorm
          ? <>{s.dorm.dorm}{s.dorm.village && <div className="fs-xs text-muted fw-normal">{s.dorm.village}</div>}</>
          : <span className="text-muted">-</span>}
      </MetaChip>
    </div>

    <div className="d-flex flex-wrap gap-2 mt-2 pt-2 border-top">
      <div>
        <div className="fs-xs text-muted mb-1">เข้าเรียน</div>
        <AxisBadge level={s.attendance.level}>ขาด {s.attendance.absent} · {s.attendance.absenceRate}%</AxisBadge>
        <div className="fs-xs text-muted mt-1">สาย {s.attendance.late} · ลา {s.attendance.leave}</div>
      </div>
      <div>
        <div className="fs-xs text-muted mb-1">การเรียน</div>
        <AxisBadge level={s.academic.level}>อ่อน {s.academic.weakSubjects} วิชา</AxisBadge>
        <div className="fs-xs text-muted mt-1">ค้างส่ง {s.academic.missingItems} งาน</div>
      </div>
      <div>
        <div className="fs-xs text-muted mb-1">ความประพฤติ</div>
        <AxisBadge level={s.behavior.level}>{s.behavior.remaining} คะแนน</AxisBadge>
        {Number(s.behavior.lowSubjects || 0) > 0 && (
          <div className="fs-xs text-danger mt-1">ต้องปรับปรุง {s.behavior.lowSubjects} วิชา</div>
        )}
      </div>
      <div>
        <div className="fs-xs text-muted mb-1">แจ้งดูแล</div>
        {Number(s.concern?.open || 0) > 0
          ? <AxisBadge level={s.concern.level}>{s.concern.open} เรื่อง</AxisBadge>
          : <span className="text-muted">-</span>}
      </div>
      <div>
        <div className="fs-xs text-muted mb-1">สุขภาพ</div>
        <div>
          {s.health.bmiFlag && <InfoTip text={`BMI ${s.health.bmi} นอกเกณฑ์`} />}
          {s.health.healthFlag
            ? <i className="ri-heart-pulse-line text-danger" title="มีข้อมูลสุขภาพ/โรคประจำตัว" />
            : <span className="text-muted">-</span>}
        </div>
      </div>
    </div>
  </div>
)

// การ์ดนักเรียน 1 คน สำหรับมุมมองมือถือ — รวมสัญญาณเดียวกับตาราง
const StudentRiskCard = ({ student: s, index, onClick }) => {
  const src = s.st_photo
    ? `${UPLOAD_BASE}${s.st_photo.startsWith('/') ? s.st_photo : `/${s.st_photo}`}`
    : null
  return (
    <Card className="h-100 shadow-sm">
      {src
        ? <Card.Img variant="top" src={src} style={{ aspectRatio: '3 / 4', width: '100%', objectFit: 'cover' }} />
        : (
          <div className="d-flex align-items-center justify-content-center bg-light text-muted" style={{ aspectRatio: '3 / 4', width: '100%' }}>
            <i className="ri-user-3-line" style={{ fontSize: 56 }} />
          </div>
        )}
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <Card.Title className="mb-0 text-truncate">{s.st_name}</Card.Title>
          <AxisBadge level={s.overall}>{RISK_META[s.overall]?.label}</AxisBadge>
        </div>
        <div className="fs-xs text-muted mb-2">#{index + 1} · {s.st_idnumber}</div>

        <Card.Text as="div">
          <div className="d-flex flex-wrap justify-content-start gap-1 pb-2 border-bottom">
            <MetaChip label="ห้องสามัญ">{s.class_room}</MetaChip>
            <MetaChip label="ห้องศาสนา">{s.religious_room || <span className="text-muted">-</span>}</MetaChip>
            <MetaChip label="หอพัก">
              {s.dorm
                ? <>{s.dorm.dorm}{s.dorm.village && <div className="fs-xs text-muted fw-normal">{s.dorm.village}</div>}</>
                : <span className="text-muted">-</span>}
            </MetaChip>
          </div>

          <div className="d-flex flex-wrap gap-2 pt-2">
            <div>
              <div className="fs-xs text-muted mb-1">เข้าเรียน</div>
              <AxisBadge level={s.attendance.level}>ขาด {s.attendance.absent} · {s.attendance.absenceRate}%</AxisBadge>
              <div className="fs-xs text-muted mt-1">สาย {s.attendance.late} · ลา {s.attendance.leave}</div>
            </div>
            <div>
              <div className="fs-xs text-muted mb-1">การเรียน</div>
              <AxisBadge level={s.academic.level}>อ่อน {s.academic.weakSubjects} วิชา</AxisBadge>
              <div className="fs-xs text-muted mt-1">ค้างส่ง {s.academic.missingItems} งาน</div>
            </div>
            <div>
              <div className="fs-xs text-muted mb-1">ความประพฤติ</div>
              <AxisBadge level={s.behavior.level}>{s.behavior.remaining} คะแนน</AxisBadge>
              {Number(s.behavior.lowSubjects || 0) > 0 && (
                <div className="fs-xs text-danger mt-1">ต้องปรับปรุง {s.behavior.lowSubjects} วิชา</div>
              )}
            </div>
            <div>
              <div className="fs-xs text-muted mb-1">แจ้งดูแล</div>
              {Number(s.concern?.open || 0) > 0
                ? <AxisBadge level={s.concern.level}>{s.concern.open} เรื่อง</AxisBadge>
                : <span className="text-muted">-</span>}
            </div>
            <div>
              <div className="fs-xs text-muted mb-1">สุขภาพ</div>
              <div>
                {s.health.bmiFlag && <InfoTip text={`BMI ${s.health.bmi} นอกเกณฑ์`} />}
                {s.health.healthFlag
                  ? <i className="ri-heart-pulse-line text-danger" title="มีข้อมูลสุขภาพ/โรคประจำตัว" />
                  : <span className="text-muted">-</span>}
              </div>
            </div>
          </div>
        </Card.Text>

        <Button variant="primary" size="sm" className="w-100 mt-auto" onClick={onClick}>ดูรายละเอียด</Button>
      </Card.Body>
    </Card>
  )
}

// ระบบติดตามนักเรียน / Early Warning — รวมสัญญาณเรียลไทม์ (ขาดเรียน + คะแนนเก็บ/งาน
// + พฤติกรรม + สุขภาพ) มาจัดอันดับความเสี่ยงรายคน โดยไม่ต้องรอเกรดออก
export default function StudentTracking() {
  const { posIds } = useAuth()
  const { setLoading } = useLoading()
  const navigate = useNavigate()
  const roleLabel = getTrackingRoleLabel(posIds)
  const levelSuffix = getLevelSuffix(posIds)

  // Dark-mode tables: mirror the other report pages — toggle `table-dark` on
  // every <thead> whenever the skin is dark (runs each render so tables that
  // mount after data loads get styled too).
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : ''
  const [skin, setSkin] = useState(currentSkin)
  const switchSkin = (s) => {
    for (const t of document.getElementsByTagName('table')) {
      t.querySelectorAll('thead').forEach(th =>
        s === 'dark' ? th.classList.add('table-dark') : th.classList.remove('table-dark'))
    }
  }
  useEffect(() => { switchSkin(skin) })

  const [yearId, setYearId] = useState(null)
  const [classList, setClassList] = useState([])
  const [roomList, setRoomList] = useState([])

  // จำตัวกรองไว้ใน sessionStorage เพื่อคืนค่าเมื่อกดย้อนกลับจากหน้ารายละเอียดนักเรียน
  const FILTERS_KEY = 'studentTracking_filters'
  const getSavedFilters = () => {
    try { return JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}') } catch { return {} }
  }

  const [semester, setSemester] = useState(() => getSavedFilters().semester ?? 'all')
  const [classId, setClassId] = useState(() => getSavedFilters().classId ?? '')
  const [roomId, setRoomId] = useState(() => getSavedFilters().roomId ?? '')
  const [minRisk, setMinRisk] = useState(() => getSavedFilters().minRisk ?? 'atrisk') // เริ่มต้น: กลุ่มเสี่ยงทั้งหมด (แดง+เหลือง)

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, red: 0, yellow: 0, green: 0, weakAcademic: 0, lowBehavior: 0, openConcerns: 0 })

  // มุมมองตาราง/การ์ด — เริ่มต้นเป็นการ์ดแบบย่อบนจอมือถือ (<768px) และให้สลับได้เอง
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'simple' : 'table')

  const goToStudent = useCallback((stId) => {
    navigate(`/academic/student-tracking/${stId}?yearId=${yearId}${semester !== 'all' ? `&semester=${semester}` : ''}`)
  }, [navigate, yearId, semester])

  // ปีการศึกษาล่าสุด
  useEffect(() => {
    axiosInstance.get('/api/getLastestStudyYear')
      .then(res => { if (res.data?.year_id) setYearId(res.data.year_id) })
      .catch(() => {})
  }, [])

  // รายการชั้น/ห้อง — server คืนเฉพาะที่ผู้ใช้คนนี้มีสิทธิ์เห็น (ครูประจำชั้นได้เฉพาะ
  // ห้องที่ตนเป็นที่ปรึกษา, หัวหน้าระดับได้เฉพาะระดับของตน) ใช้ scope เดียวกับตารางความเสี่ยง
  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`/api/tracking/filterOptions/${yearId}`)
      .then(res => {
        setClassList(res.data?.classes || [])
        setRoomList(res.data?.rooms || [])
      })
      .catch(() => {})
  }, [yearId])

  // ตัวกรองที่จำไว้อาจชี้ไปชั้น/ห้องที่ผู้ใช้คนนี้ไม่มีสิทธิ์เห็น (เช่น เคยเป็นหัวหน้าระดับ
  // หรือใช้เครื่องร่วมกัน) — ล้างทิ้งเพื่อไม่ให้ตารางว่างโดยไม่รู้สาเหตุ
  useEffect(() => {
    if (classList.length && classId && !classList.some(c => String(c.c_id) === String(classId))) setClassId('')
  }, [classList, classId])
  useEffect(() => {
    if (roomList.length && roomId && !roomList.some(r => String(r.r_id) === String(roomId))) setRoomId('')
  }, [roomList, roomId])

  // ล้างห้องเมื่อเปลี่ยนชั้น แต่ข้ามรอบแรก เพื่อไม่ให้ทับค่าห้องที่คืนมาจาก sessionStorage
  const isFirstClassRender = useRef(true)
  useEffect(() => {
    if (isFirstClassRender.current) { isFirstClassRender.current = false; return }
    setRoomId('')
  }, [classId])

  // บันทึกตัวกรองปัจจุบันไว้ทุกครั้งที่เปลี่ยน เพื่อคืนค่าเมื่อกดย้อนกลับ
  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ semester, classId, roomId, minRisk }))
  }, [semester, classId, roomId, minRisk])

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (semester !== 'all') p.append('semester', semester)
    if (classId) p.append('classId', classId)
    if (roomId) p.append('roomId', roomId)
    if (minRisk) p.append('minRisk', minRisk)
    return p.toString()
  }, [semester, classId, roomId, minRisk])

  const fetchData = useCallback(() => {
    if (!yearId) return
    setLoading(true)
    Promise.allSettled([
      axiosInstance.get(`/api/tracking/riskList/${yearId}?${queryString}`),
      axiosInstance.get(`/api/tracking/summary/${yearId}?${queryString}`),
    ]).then(([listRes, sumRes]) => {
      if (listRes.status === 'fulfilled') setRows(listRes.value.data || [])
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data || {})
    }).finally(() => setLoading(false))
  }, [yearId, queryString, setLoading])

  useEffect(() => { fetchData() }, [fetchData])

  // อัปเดตเรียลไทม์เมื่อมีการเช็คชื่อ (ใช้ event เดิมของระบบเช็คชื่อ)
  useEffect(() => {
    if (!yearId) return
    const socket = io(process.env.REACT_APP_SOCKET_URL)
    socket.on('refreshGeneralAttendance', () => fetchData())
    return () => socket.disconnect()
  }, [yearId, fetchData])

  const filteredRooms = useMemo(
    () => roomList.filter(r => !classId || r.c_id === parseInt(classId, 10)),
    [roomList, classId]
  )

  const summaryCards = [
    { key: 'red',    label: 'เสี่ยงสูง',      value: summary.red,    bg: 'danger',  icon: 'ri-alarm-warning-line' },
    { key: 'yellow', label: 'เฝ้าระวัง',      value: summary.yellow, bg: 'warning', icon: 'ri-eye-line' },
    { key: 'weak',   label: 'อ่อนวิชา',        value: summary.weakAcademic, bg: 'info', icon: 'ri-book-2-line' },
    { key: 'beh',    label: 'พฤติกรรมต่ำกว่าเกณฑ์', value: summary.lowBehavior, bg: 'secondary', icon: 'ri-shield-user-line' },
    { key: 'care',   label: 'ถูกแจ้งดูแล',     value: summary.openConcerns, bg: 'primary', icon: 'ri-flag-2-line' },
  ]

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        {/* หัวเรื่อง */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabel}</Link></li>
              <li className="breadcrumb-item active">ระบบติดตามนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">
              <i className="ri-radar-line me-2 text-primary" />
              ระบบติดตามนักเรียน (Early Warning){levelSuffix}
            </h4>
            <p className="text-muted fs-sm mb-0">
              รวมสัญญาณเตือนแบบเรียลไทม์ — ขาดเรียน · คะแนนเก็บ/งานที่ยังไม่ส่ง · พฤติกรรม · แจ้งดูแล · สุขภาพ
            </p>
          </div>
        </div>

        {/* การ์ดสรุป */}
        <Row className="g-3 mb-4">
          {summaryCards.map(c => (
            <Col key={c.key} xs={6} md={4} xl>
              <Card className="card-one">
                <Card.Body className="d-flex align-items-center gap-3 py-3">
                  <div className={`d-flex align-items-center justify-content-center rounded bg-${c.bg} text-white`} style={{ width: 44, height: 44 }}>
                    <i className={`${c.icon} fs-4`} />
                  </div>
                  <div>
                    <h3 className="mb-0">{c.value ?? 0}</h3>
                    <span className="text-muted fs-sm">{c.label}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ตัวกรอง */}
        <Card className="card-one mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ภาคเรียน</Form.Label>
                <Form.Select size="sm" value={semester} onChange={e => setSemester(e.target.value)}>
                  <option value="all">ทั้งปี</option>
                  <option value="1">ภาคเรียนที่ 1</option>
                  <option value="2">ภาคเรียนที่ 2</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ชั้น</Form.Label>
                <Form.Select size="sm" value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {classList.map(c => <option key={c.c_id} value={c.c_id}>{c.c_class || `ม.${c.c_id}`}</option>)}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-sm mb-1">ห้องเรียน</Form.Label>
                <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)} disabled={!classId}>
                  <option value="">ทั้งหมด</option>
                  {filteredRooms.map(r => <option key={r.r_id} value={r.r_id}>{r.c_id}/{r.r_no}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="fs-sm mb-1">ระดับความเสี่ยง</Form.Label>
                <div className="btn-group btn-group-sm w-100">
                  {[['red', 'เสี่ยงสูง'], ['yellow', 'เฝ้าระวัง'], ['atrisk', 'ทั้งหมด']].map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn btn${minRisk === v ? '' : '-outline'}-primary`}
                      onClick={() => setMinRisk(v)}
                    >{label}</button>
                  ))}
                </div>
              </Col>
              <Col md={3} className="text-md-end">
                <Badge bg="light" text="dark" className="fs-sm">พบ {rows.length} คน</Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ตารางจัดอันดับ */}
        <Card className="card-one">
          <Card.Header className="d-flex align-items-center justify-content-between gap-2">
            <Card.Title as="h6" className="mb-0">จัดอันดับนักเรียนตามความเสี่ยงรวม</Card.Title>
            <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
              <button
                type="button"
                className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setView('table')}
                title="มุมมองตาราง"
              ><i className="ri-table-line" /></button>
              <button
                type="button"
                className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setView('card')}
                title="มุมมองการ์ด (รูปภาพ)"
              ><i className="ri-layout-grid-line" /></button>
              <button
                type="button"
                className={`btn btn${view === 'simple' ? '' : '-outline'}-primary`}
                onClick={() => setView('simple')}
                title="มุมมองการ์ด (แบบย่อ)"
              ><i className="ri-list-check-2" /></button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {rows.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="ri-shield-check-line fs-2 d-block mb-2" />
                ไม่พบนักเรียนที่เข้าเกณฑ์ในช่วงที่เลือก
              </div>
            ) : view === 'card' ? (
              <div style={{ maxHeight: 640, overflowY: 'auto' }} className="p-3">
                <Row className="g-3">
                  {rows.map((s, idx) => (
                    <Col key={s.st_id} xs={12} sm={6} lg={4} xl={3}>
                      <StudentRiskCard student={s} index={idx} onClick={() => goToStudent(s.st_id)} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : view === 'simple' ? (
              <div style={{ maxHeight: 640, overflowY: 'auto' }} className="p-3">
                <Row className="g-3">
                  {rows.map((s, idx) => (
                    <Col key={s.st_id} xs={12} md={4} xl={3}>
                      <StudentRiskCardSimple student={s} index={idx} onClick={() => goToStudent(s.st_id)} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div style={{ maxHeight: 640, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0 align-middle">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th className="text-center" style={{ width: 44 }}>#</th>
                      <th style={{ minWidth: 200 }}>นักเรียน</th>
                      <th className="text-center" style={{ width: 70 }}>ห้องสามัญ</th>
                      <th className="text-center" style={{ width: 80 }}>ห้องศาสนา</th>
                      <th className="text-center" style={{ minWidth: 110 }}>หอพัก</th>
                      <th className="text-center" style={{ width: 110 }}>
                        ความเสี่ยง<InfoTip text="ระดับรวม = แกนที่แย่ที่สุดใน 4 ด้าน (เข้าเรียน/การเรียน/พฤติกรรม/แจ้งดูแล)" />
                      </th>
                      <th className="text-center" style={{ width: 150 }}>
                        เข้าเรียน<InfoTip text="ขาด/สาย/ลา และ % การขาด — แดง >20%, เหลือง 10–20%" />
                      </th>
                      <th className="text-center" style={{ width: 150 }}>
                        การเรียน<InfoTip text="จำนวนวิชาที่คะแนนเก็บอ่อน (<50%) และจำนวนงานที่ยังไม่ส่ง" />
                      </th>
                      <th className="text-center" style={{ width: 110 }}>
                        ความประพฤติ<InfoTip text="คะแนนความประพฤติที่เหลือจาก 100" />
                      </th>
                      <th className="text-center" style={{ width: 90 }}>
                        แจ้งดูแล<InfoTip text="เรื่องที่ครูแจ้งดูแลและยังไม่ยุติ" />
                      </th>
                      <th className="text-center" style={{ width: 70 }}>สุขภาพ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s, idx) => (
                      <tr
                        key={s.st_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => goToStudent(s.st_id)}
                      >
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={s.st_photo} name={s.st_name} />
                            <div>
                              <div className="fw-semibold">{s.st_name}</div>
                              <div className="fs-xs text-muted">{s.st_idnumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center fs-sm">{s.class_room}</td>
                        <td className="text-center fs-sm">
                          {s.religious_room || <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center fs-sm">
                          {s.dorm
                            ? <>
                                <div>{s.dorm.dorm}</div>
                                {s.dorm.village && <div className="fs-xs text-muted">{s.dorm.village}</div>}
                              </>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          <AxisBadge level={s.overall}>{RISK_META[s.overall]?.label}</AxisBadge>
                        </td>
                        <td className="text-center">
                          <AxisBadge level={s.attendance.level}>
                            ขาด {s.attendance.absent} · {s.attendance.absenceRate}%
                          </AxisBadge>
                          <div className="fs-xs text-muted mt-1">สาย {s.attendance.late} · ลา {s.attendance.leave}</div>
                        </td>
                        <td className="text-center">
                          <AxisBadge level={s.academic.level}>
                            อ่อน {s.academic.weakSubjects} วิชา
                          </AxisBadge>
                          <div className="fs-xs text-muted mt-1">ค้างส่ง {s.academic.missingItems} งาน</div>
                        </td>
                        <td className="text-center">
                          <AxisBadge level={s.behavior.level}>{s.behavior.remaining} คะแนน</AxisBadge>
                          {Number(s.behavior.lowSubjects || 0) > 0 && (
                            <div className="fs-xs text-danger mt-1">ต้องปรับปรุง {s.behavior.lowSubjects} วิชา</div>
                          )}
                        </td>
                        <td className="text-center">
                          {Number(s.concern?.open || 0) > 0
                            ? <AxisBadge level={s.concern.level}>{s.concern.open} เรื่อง</AxisBadge>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {s.health.bmiFlag && <InfoTip text={`BMI ${s.health.bmi} นอกเกณฑ์`} />}
                          {s.health.healthFlag
                            ? <i className="ri-heart-pulse-line text-danger" title="มีข้อมูลสุขภาพ/โรคประจำตัว" />
                            : <span className="text-muted">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
      <Footer />
    </React.Fragment>
  )
}
